import axios from "axios";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { Context } from "../../context/context";
import { Button, Card, Chip, EmptyState, Field, Modal, PageSpinner, TextInput } from "../../components/ui";
import {
  AlertIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DeviceIcon,
  MailUnreadIcon,
  PlusIcon,
  SyncIcon,
  SyncProblemIcon,
} from "../../components/ui/icons";

const Devices = () => {
  const [myDevices, setMyDevices] = useState(null);
  const [networkMemberDevices, setNetworkMemberDevices] = useState([]);
  const [networkViewerDevices, setNetworkViewerDevices] = useState([]);
  const [invites, setInvites] = useState([]);

  const [showInvitationsModal, setShowInvitationsModal] = useState(false);
  const [inviteModalIndex, setInviteModalIndex] = useState(0);
  const [invitedDeviceNickname, setInvitedDeviceNickname] = useState("");

  const [showAddNewDeviceModal, setShowAddNewDeviceModal] = useState(false);
  const [newDeviceID, setNewDeviceID] = useState("");
  const [newDeviceNickname, setNewDeviceNickname] = useState("");
  const [isBusy, setIsBusy] = useState(false);

  const { state, dispatch } = useContext(Context);
  const navigate = useNavigate();

  const loadAll = useCallback(async () => {
    try {
      const [mine, members, viewers, pending] = await Promise.all([
        axios.post("/app-api/user/mydevices"),
        axios.post("/app-api/user/networkdevices", { targetRole: "Member" }),
        axios.post("/app-api/user/networkdevices", { targetRole: "Viewer" }),
        axios.get("/app-api/user/invites"),
      ]);
      setMyDevices(mine.data?.devices || []);
      setNetworkMemberDevices(members.data?.devices || []);
      setNetworkViewerDevices(viewers.data?.devices || []);
      setInvites(pending.data?.invites || []);
    } catch (err) {
      console.error(err);
      setMyDevices([]);
      toast.error("Could not load your devices");
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    setInviteModalIndex(0);
  }, [showInvitationsModal]);

  useEffect(() => {
    setInvitedDeviceNickname("");
  }, [showInvitationsModal, inviteModalIndex]);

  const rememberNickname = (deviceId, nickname) => {
    const nicknames = { ...state.user.nicknames, [deviceId]: nickname };
    dispatch({ type: "CHANGE_NICKNAME", payload: { ...state.user, nicknames } });
  };

  const closeInviteAt = (index) => {
    if (index > 0) setInviteModalIndex(index - 1);
    else setShowInvitationsModal(false);
    setInvites((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAcceptInvite = async () => {
    if (!invitedDeviceNickname.trim()) return toast.error("Enter a nickname for the device");
    const index = inviteModalIndex;
    const invite = invites[index];
    setIsBusy(true);
    try {
      await axios.post("/app-api/user/accept-invite", {
        inviteId: invite.id,
        nickname: invitedDeviceNickname.trim(),
      });
      const entry = { id: invite.deviceId, nickname: invitedDeviceNickname.trim() };
      if (invite.targetRole === "Member") setNetworkMemberDevices((prev) => [...prev, entry]);
      else setNetworkViewerDevices((prev) => [...prev, entry]);
      rememberNickname(invite.deviceId, invitedDeviceNickname.trim());
      closeInviteAt(index);
      toast.success("Invitation accepted");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data || "Could not accept the invitation");
    } finally {
      setIsBusy(false);
    }
  };

  const handleRejectInvite = async () => {
    const index = inviteModalIndex;
    setIsBusy(true);
    try {
      await axios.post("/app-api/user/reject-invite", { inviteId: invites[index].id });
      closeInviteAt(index);
      toast.success("Invitation rejected");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data || "Could not reject the invitation");
    } finally {
      setIsBusy(false);
    }
  };

  const handleAddNewDevice = async () => {
    if (!newDeviceID.trim()) return toast.error("Please enter a device ID");
    if (!newDeviceNickname.trim()) return toast.error("Please enter a nickname");
    setIsBusy(true);
    try {
      await axios.post("/app-api/user/add-device", {
        deviceId: newDeviceID.trim(),
        nickname: newDeviceNickname.trim(),
      });
      setMyDevices((prev) => [
        ...prev,
        {
          id: newDeviceID.trim(),
          nickname: newDeviceNickname.trim(),
          hasRecentMissedPill: false,
          sync: { status: true, syncedTime: null, updatedTime: null },
        },
      ]);
      rememberNickname(newDeviceID.trim(), newDeviceNickname.trim());
      setShowAddNewDeviceModal(false);
      setNewDeviceID("");
      setNewDeviceNickname("");
      toast.success("Device added");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data || "Could not add the device");
    } finally {
      setIsBusy(false);
    }
  };

  if (!myDevices) return <PageSpinner />;

  const currentInvite = invites[inviteModalIndex];
  const hasAnyDevice = myDevices.length + networkMemberDevices.length + networkViewerDevices.length > 0;

  return (
    <>
      {invites.length > 0 && (
        <Card className="mb-6 flex flex-wrap items-center gap-3 border-primary-200 bg-primary-50 p-4">
          <MailUnreadIcon className="h-5 w-5 flex-shrink-0 text-primary-600" />
          <p className="min-w-0 flex-1 text-sm font-semibold text-primary-900">
            You have {invites.length} pending invitation{invites.length > 1 ? "s" : ""}
          </p>
          <Button size="sm" onClick={() => setShowInvitationsModal(true)}>
            Open invitation{invites.length > 1 ? "s" : ""}
          </Button>
        </Card>
      )}

      <DeviceSection
        title="My Devices"
        devices={myDevices}
        onOpen={(id) => navigate(`/device/${id}/dashboard`)}
        action={
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {/* <button
              onClick={() => navigate("/wifi-setup")}
              className="text-xs font-semibold text-primary-600 hover:underline"
            >
              Setting up a new device? Connect it to WiFi first
            </button> */}
            <Button size="sm" onClick={() => setShowAddNewDeviceModal(true)}>
              <PlusIcon className="h-4 w-4" />
              Add device
            </Button>
          </div>
        }
        emptyMessage="Add your first MedpeR to start tracking doses."
      />

      {networkMemberDevices.length > 0 && (
        <DeviceSection
          title="Shared with me"
          subtitle="You can manage schedules on these devices"
          devices={networkMemberDevices}
          role="Member"
          onOpen={(id) => navigate(`/device/${id}/dashboard`)}
        />
      )}

      {networkViewerDevices.length > 0 && (
        <DeviceSection
          title="Shared with me — view only"
          subtitle="You can see reports and alerts on these devices"
          devices={networkViewerDevices}
          role="Viewer"
          onOpen={(id) => navigate(`/device/${id}/dashboard`)}
        />
      )}

      {!hasAnyDevice && invites.length === 0 && (
        <Card>
          <EmptyState
            icon={DeviceIcon}
            title="No devices yet"
            description="Add a MedpeR using the device ID printed on the base of the unit."
            action={
              <div className="flex flex-col items-center gap-3">
                <Button size="sm" onClick={() => setShowAddNewDeviceModal(true)}>
                  <PlusIcon className="h-4 w-4" />
                  Add device
                </Button>
                {/* <button onClick={() => navigate("/wifi-setup")} className="text-xs font-semibold text-primary-600 hover:underline">
                  Setting up a new device? Connect it to WiFi first
                </button> */}
              </div>
            }
          />
        </Card>
      )}

      {/* --- invitations --- */}
      <Modal
        isOpen={showInvitationsModal && Boolean(currentInvite)}
        onRequestClose={() => setShowInvitationsModal(false)}
        title={`Invitation ${inviteModalIndex + 1} of ${invites.length}`}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={handleRejectInvite} loading={isBusy}>
              Reject
            </Button>
            <Button onClick={handleAcceptInvite} loading={isBusy}>
              Accept
            </Button>
          </>
        }
      >
        {currentInvite && (
          <>
            <p className="text-sm text-gray-600">
              <span className="font-semibold text-primary-900">{currentInvite.invitedBy?.displayName || "A MedpeR user"}</span>
              {currentInvite.invitedBy?.email && <span className="text-xs text-gray-400"> ({currentInvite.invitedBy.email})</span>} invited you to
              device <span className="font-mono font-semibold text-primary-900">{currentInvite.deviceId}</span> as{" "}
              <Chip tone="brand">{currentInvite.targetRole}</Chip>
            </p>

            <Field label="Give this device a nickname" className="mt-5" htmlFor="invite-nickname" hint="Only you will see this name.">
              <TextInput
                id="invite-nickname"
                type="text"
                autoFocus
                value={invitedDeviceNickname}
                placeholder="e.g. Amma's MedpeR"
                onChange={(e) => setInvitedDeviceNickname(e.target.value)}
              />
            </Field>

            {invites.length > 1 && (
              <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4">
                <button
                  disabled={inviteModalIndex === 0}
                  onClick={() => setInviteModalIndex((i) => i - 1)}
                  className="flex items-center gap-1 text-xs font-semibold text-primary-600 disabled:text-gray-300"
                >
                  <ChevronLeftIcon className="h-3.5 w-3.5" />
                  Previous
                </button>
                <button
                  disabled={inviteModalIndex >= invites.length - 1}
                  onClick={() => setInviteModalIndex((i) => i + 1)}
                  className="flex items-center gap-1 text-xs font-semibold text-primary-600 disabled:text-gray-300"
                >
                  Next
                  <ChevronRightIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* --- add device --- */}
      <Modal
        isOpen={showAddNewDeviceModal}
        onRequestClose={() => setShowAddNewDeviceModal(false)}
        title="Add new device"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShowAddNewDeviceModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddNewDevice} loading={isBusy}>
              Add
            </Button>
          </>
        }
      >
        <Field label="Device ID" htmlFor="new-device-id" hint="Printed on the base of the unit.">
          <TextInput
            id="new-device-id"
            type="text"
            autoFocus
            value={newDeviceID}
            placeholder="e.g. MPR-101"
            onChange={(e) => setNewDeviceID(e.target.value)}
          />
        </Field>
        <Field label="Nickname" htmlFor="new-device-nickname">
          <TextInput
            id="new-device-nickname"
            type="text"
            value={newDeviceNickname}
            placeholder="e.g. Amma's MedpeR"
            onChange={(e) => setNewDeviceNickname(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddNewDevice()}
          />
        </Field>
      </Modal>
    </>
  );
};

const DeviceSection = ({ title, subtitle, devices, role, onOpen, action, emptyMessage }) => (
  <section className="mb-8 last:mb-0">
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 className="text-lg font-bold text-primary-900">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
      </div>
      {action}
    </div>

    {devices.length === 0 ? (
      emptyMessage && <p className="text-sm text-gray-500">{emptyMessage}</p>
    ) : (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {devices.map((device) => (
          <button
            key={device.id}
            onClick={() => onOpen(device.id)}
            className="card p-5 text-left transition hover:border-primary-200 hover:shadow-card"
          >
            {role && (
              <div className="mb-2 flex justify-end">
                <Chip tone="neutral">{role}</Chip>
              </div>
            )}
            <p className="truncate text-sm font-bold text-primary-900">{device.nickname || device.id}</p>
            <p className="mt-1 truncate font-mono text-base font-semibold text-gray-700">{device.id}</p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {device.sync?.status ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-teal-600">
                  <SyncIcon className="h-3.5 w-3.5" />
                  Synced
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-600">
                  <SyncProblemIcon className="h-3.5 w-3.5" />
                  Not synced
                </span>
              )}
              {device.hasRecentMissedPill && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600">
                  <AlertIcon className="h-3.5 w-3.5" />
                  Recent missed dose
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    )}
  </section>
);

export default Devices;
